import { useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useApi } from '../lib/useApi';
import { money } from '../lib/format';
import { COUNTIES_BY_USE } from '../lib/counties';
import { ExploreTabs } from '../components/ExploreTabs';
import { ExploreHero } from '../components/ExploreHero';
import { CategoryGrid } from '../components/CategoryGrid';
import { PublicHeader } from '../components/PublicHeader';
import { SiteFooter } from '../components/SiteFooter';
import {
  Button, EmptyState, ErrorBlock, Field, LoadingBlock, Select, TextInput,
} from '../components/ui';
import '../styles/public.css';

/**
 * Explore · Marketplace (specification §32).
 *
 * Conventional and on purpose: search, category, location, price, condition,
 * then cards. §33 asks the three Explore sections to look like one another, so
 * this borrows the services grid rather than inventing a third layout.
 *
 * Sold items appear here, marked and sunk to the bottom. They are not noise —
 * somebody who saw the advert yesterday and comes back today is owed the
 * answer, and a vanished listing tells them nothing.
 */

interface Category { id: string; slug: string; name: string; items: number }
interface Item {
  id: string; slug: string; title: string; description: string | null;
  price: string | null; condition: string | null;
  county: string; town: string | null; seller_name: string;
  phone: string; whatsapp: string | null; status: 'approved' | 'sold';
  is_featured: boolean; category_slug: string; category_name: string;
}

const CONDITIONS = [
  ['', 'Any condition'], ['new', 'New'], ['like_new', 'Like new'],
  ['good', 'Good'], ['fair', 'Fair'], ['for_parts', 'For parts'],
] as const;

const conditionLabel = (c: string | null) =>
  CONDITIONS.find(([v]) => v === c)?.[1] ?? null;

/** Absent price is "contact for price", not free. Zero IS free. */
const priceLabel = (p: string | null) =>
  p === null ? 'Contact for price' : Number(p) === 0 ? 'Free' : money(p);

export function Marketplace() {
  const { pathname } = useLocation();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('q') ?? '');

  const category = params.get('category') ?? '';
  const county = params.get('county') ?? '';
  const condition = params.get('condition') ?? '';
  const max = params.get('max') ?? '';
  const q = params.get('q') ?? '';

  const cats = useApi<{ categories: Category[] }>('/api/v1/public/marketplace/categories');
  const query = useMemo(() => {
    const p = new URLSearchParams();
    for (const [k, v] of [['category', category], ['county', county],
      ['condition', condition], ['max', max], ['q', q]] as const) {
      if (v) p.set(k, v);
    }
    return p.toString();
  }, [category, county, condition, max, q]);
  const list = useApi<{ items: Item[]; total: number }>(
    `/api/v1/public/marketplace${query ? `?${query}` : ''}`);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <>
      <PublicHeader />
      <main className="page public-page">
        <ExploreTabs path={pathname} />

        <ExploreHero
          section="market"
          title="Marketplace"
          sub="Furniture, appliances and household things, bought and sold locally."
        >
          <form
            className="xhero-search"
            onSubmit={(e) => { e.preventDefault(); setParam('q', search.trim()); }}
          >
          <div style={{ minWidth: 180 }}>
            <Field label="Category" htmlFor="mp-cat">
              <Select id="mp-cat" value={category} onChange={(e) => setParam('category', e.target.value)}>
                <option value="">All categories</option>
                {(cats.data?.categories ?? []).map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.name}{c.items ? ` (${c.items})` : ''}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div style={{ minWidth: 150 }}>
            <Field label="County" htmlFor="mp-county">
              <Select id="mp-county" value={county}
                onChange={(e) => setParam('county', e.target.value)}>
                <option value="">Any county</option>
                {COUNTIES_BY_USE.map((c) => <option key={c} value={c}>{c}</option>)}
              </Select>
            </Field>
          </div>
          <div style={{ minWidth: 150 }}>
            <Field label="Condition" htmlFor="mp-cond">
              <Select id="mp-cond" value={condition} onChange={(e) => setParam('condition', e.target.value)}>
                {CONDITIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </Select>
            </Field>
          </div>
          <div style={{ minWidth: 130 }}>
            <Field label="Max price" htmlFor="mp-max">
              <TextInput id="mp-max" inputMode="numeric" value={max} placeholder="Any"
                onChange={(e) => setParam('max', e.target.value.replace(/[^0-9]/g, ''))} />
            </Field>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <Field label="Search" htmlFor="mp-q">
              <TextInput id="mp-q" value={search} placeholder="What are you looking for?"
                onChange={(e) => setSearch(e.target.value)} />
            </Field>
          </div>
            <Button type="submit">Search</Button>
          </form>
        </ExploreHero>

        <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 16 }}>
          <Link className="btn btn-primary" to="/marketplace/post">Sell something</Link>
        </div>

        {!q && (
          <CategoryGrid
            section="market"
            categories={(cats.data?.categories ?? []).map(c => ({
              slug: c.slug, name: c.name, count: c.items ?? 0,
            }))}
            active={category}
            onPick={(slug) => setParam('category', slug)}
          />
        )}

        {list.loading ? <LoadingBlock />
          : list.error ? <ErrorBlock error={list.error} onRetry={list.reload} />
          : (list.data?.items.length ?? 0) === 0 ? (
            <EmptyState
              title="Nothing for sale here yet"
              message={query ? 'Try a different category or county.'
                : 'Be the first. Posting something is free.'}
            />
          ) : (
            <>
              <p className="muted" style={{ marginBottom: 12 }}>
                {list.data!.total} item{list.data!.total === 1 ? '' : 's'}
              </p>
              <div className="provider-grid">
                {list.data!.items.map((i) => (
                  <article key={i.id} className="provider-card provider-card--market">
                    <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                      {i.status === 'sold' && <span className="provider-sold">Sold</span>}
                      {i.is_featured && i.status !== 'sold' && (
                        <span className="provider-featured">Featured</span>
                      )}
                    </div>
                    <div className="provider-head">
                      <span className="sect-tile sect-market provider-mono" aria-hidden>
                        {i.title.trim().charAt(0).toUpperCase()}
                      </span>
                      <h3><Link to={`/marketplace/${i.slug}`}>{i.title}</Link></h3>
                    </div>
                    <p className="provider-price">{priceLabel(i.price)}</p>
                    <p className="provider-meta">
                      {i.category_name}
                      {conditionLabel(i.condition) ? ` · ${conditionLabel(i.condition)}` : ''}
                      {' · '}{i.town ? `${i.town}, ` : ''}{i.county}
                    </p>
                    {i.description && (
                      <p style={{ margin: 0, fontSize: 'var(--fs-body-sm)' }}>
                        {i.description.length > 120 ? `${i.description.slice(0, 120)}…` : i.description}
                      </p>
                    )}
                    <div className="provider-actions">
                      {i.status === 'sold'
                        ? <span className="muted">No longer available</span>
                        : <a className="btn btn-secondary btn-sm" href={`tel:${i.phone}`}>Call seller</a>}
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
      </main>
      <SiteFooter />
    </>
  );
}
